import { z } from 'zod';
// ── Agent registry contract ─────────────────────────────────────────────────
// The shared, framework-agnostic shape for a reusable AI "agent" (a labeled
// model + prompt + guardrails). The agent-binding / run-mode vocabularies mirror
// the model Concord locked in its integrations-library PRD so the two products
// can extract a shared package later — keep these values BYTE-IDENTICAL across
// repos; record any deliberate divergence in the PRD's Cross-repo reuse section.
//
// Note: an action carries `agents[]` (0..n, like sources/destinations) and
// `runModes` — they live on `ActionDef`, NOT on the agent. The agent is just
// the reusable brain (model + prompt + guardrails); an action with an empty
// `agents[]` has no AI. AI is a property (having agents), not a category.
// Multi-agent actions describe their agents as "working together" — never as
// an ordered/numbered sequence.
/** How one agent participates in an action: always runs, or user-toggleable. */
export const ACTION_AGENT_BINDINGS = ['required', 'optional'];
/** How an action / automation can be invoked. Orthogonal to AI. */
export const RUN_MODES = ['once', 'schedule', 'trigger'];
/**
 * Symmetric I/O vocabulary. An action reads Sources[] and writes Destinations[].
 * A `project` is the first source type (integrations plug in later). Destinations
 * subsume the old "notify task" + "Then" step — `notify` is one type. Lives here
 * (the shared vocab module) so `actions.ts` + the catalog can't drift.
 */
export const SOURCE_TYPES = ['project', 'integration'];
export const DESTINATION_TYPES = [
    'notify',
    'webhook',
    'crm',
    'action',
    'internal',
];
export const NOTIFY_CHANNELS = [
    'slack',
    'email',
    'discord',
    'telegram',
];
// The plan feature a notify channel or the webhook destination needs; email is free.
// One definition serves the backend save/delivery gates and the admin pickers.
export const DESTINATION_CHANNEL_FEATURE = {
    slack: 'alerts',
    telegram: 'alerts',
    discord: 'alerts',
    webhook: 'webhooks',
};
/**
 * The per-kind context an agent injects (icp = the Company Profile's ICP
 * narrative; summary = tone/format guidance). The org-wide AI-memory layer
 * that once backed these was removed in favor of per-action memory_override.
 */
export const AGENT_CONTEXT_KINDS = ['icp', 'summary'];
// ── Agents list wire DTO (Logspot admin API; snake_case) ─────────────────────
// The read model the `Actions › Agents` page consumes: each agent plus the
// billable actions it powers. NOT part of the cross-repo `Agent` contract above
// — this is Logspot's response shape, served by `GET /api/admin/agents`.
export const agentActionRefSchema = z.object({
    key: z.string(),
    label: z.string(),
    // This agent's binding ON that action (required = always runs; optional =
    // user-toggleable via agent_overrides).
    agent_binding: z.enum(ACTION_AGENT_BINDINGS),
    run_modes: z.array(z.enum(RUN_MODES)),
});
export const agentCatalogEntrySchema = z.object({
    key: z.string(),
    label: z.string(),
    description: z.string(),
    model: z.string().nullable(),
    context_kind: z.string().nullable(),
    recommends_profile: z.boolean(),
    guardrails: z.array(z.string()),
    actions: z.array(agentActionRefSchema),
});
export const agentsListResponseSchema = z.object({
    agents: z.array(agentCatalogEntrySchema),
});
// ── Action catalog wire DTO (Browse Catalog) ─────────────────────────────────
// One entry per billable action. `agents` are the 0..n agents that power it
// (empty for lookups/templates); multi-agent actions present them as "working
// together", never as an ordered sequence. `tools` is the deduped effective
// tool set (the action's own direct tools ∪ every attached agent's tools).
// `requires_web_search` is DERIVED from that set; such actions are hidden from
// customers in the catalog when no web-search provider is configured (see
// web_search_available on the list response); super-admins always see them.
/** One agent attached to an action, resolved for the UI. */
export const actionAgentRefSchema = z.object({
    key: z.string(),
    label: z.string(),
    // What THIS agent does — shown under its name (never generic shared copy).
    description: z.string(),
    binding: z.enum(ACTION_AGENT_BINDINGS),
});
/** One tool in an action's effective tool set, resolved for the UI. */
export const actionToolRefSchema = z.object({
    key: z.string(),
    label: z.string(),
    description: z.string().optional(),
});
/**
 * Catalog list price (NOT org-resolved — promos / per-org overrides resolve at
 * run time). `data_credits` 'variable' = settled from actual LLM token usage.
 * Display is gated behind the `actionsCredits` feature flag client-side.
 */
export const actionCostSchema = z.object({
    actions: z.number(),
    data_credits: z.union([z.number(), z.literal('variable')]),
    // Credits-only preview for VARIABLE actions: the expected Data-Credit cost
    // plus the reserve ceiling (`max`), quoted at the org's default model and the
    // managed markup so it tracks what a run settles. Present ONLY for variable
    // actions; omitted for fixed ones (their `data_credits` is already exact).
    data_credits_estimate: z
        .object({ expected: z.number(), max: z.number() })
        .optional(),
});
export const actionCatalogEntrySchema = z.object({
    key: z.string(),
    label: z.string(),
    // Short descriptor under the title (icon-row balance), e.g. 'Company research'.
    subtitle: z.string().nullable(),
    description: z.string().nullable(),
    // "What You Get" deliverable chips for the detail view.
    what_you_get: z.array(z.string()),
    kind: z.string(), // lookup | ai | template
    agents: z.array(actionAgentRefSchema),
    tools: z.array(actionToolRefSchema),
    cost: actionCostSchema,
    run_modes: z.array(z.enum(RUN_MODES)),
    feature_key: z.string().optional(),
    project_only: z.boolean(),
    requires_web_search: z.boolean(),
    // Can be executed straight from its saved config ("Run once now") with no
    // extra per-run input; the other AI actions run from their own forms.
    runnable_from_config: z.boolean(),
    // Which Source / Destination types this action accepts — drives the Set Up
    // pickers. `project` sources are the org/project scope today; destinations
    // default to none for tasks with only internal output.
    accepted_sources: z.array(z.enum(SOURCE_TYPES)),
    accepted_destinations: z.array(z.enum(DESTINATION_TYPES)),
    // Entity-maintenance verbs (profile.build, account.watch): present in the
    // response so saved instances resolve their entry (Your Actions rows, the
    // configure dialog), but filtered from the Browse Catalog grid.
    hidden_from_catalog: z.boolean().optional(),
});
export const actionCatalogListResponseSchema = z.object({
    actions: z.array(actionCatalogEntrySchema),
    // Whether an outbound web-search provider is configured (drives client-side
    // hiding of `requires_web_search` actions for non-super-admins).
    web_search_available: z.boolean(),
});
//# sourceMappingURL=agents.js.map